type ReportFileErrorCode = 'URL_EXPIRED' | 'DOWNLOAD_FAILED' | 'INVALID_FILE'

export class ReportFileError extends Error {
  readonly code: ReportFileErrorCode

  constructor(code: ReportFileErrorCode) {
    super(code)
    this.name = 'ReportFileError'
    this.code = code
  }
}

export const downloadReportFile = async (
  file: { downloadUrl: string; filename: string; urlExpiresAt: string },
  options: { signal?: AbortSignal; isCurrent?: () => boolean } = {}
): Promise<void> => {
  const isCurrent = options.isCurrent || (() => true)
  const throwIfAborted = () => {
    if (options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }
  }

  if (!isCurrent()) return
  throwIfAborted()

  let downloadURL: URL
  try {
    downloadURL = new URL(file.downloadUrl)
  } catch {
    throw new ReportFileError('INVALID_FILE')
  }
  const expiresAt = Date.parse(file.urlExpiresAt)
  if (
    downloadURL.protocol !== 'https:' ||
    downloadURL.username ||
    downloadURL.password ||
    !file.filename.trim() ||
    !Number.isFinite(expiresAt)
  ) {
    throw new ReportFileError('INVALID_FILE')
  }
  if (expiresAt <= Date.now()) {
    throw new ReportFileError('URL_EXPIRED')
  }

  try {
    const response = await fetch(file.downloadUrl, {
      method: 'GET',
      credentials: 'omit',
      signal: options.signal,
    })
    if (!isCurrent()) return
    throwIfAborted()
    if (!response.ok) {
      throw new ReportFileError('DOWNLOAD_FAILED')
    }

    const contentType = (response.headers.get('content-type') || '')
      .split(';')[0]
      .trim()
      .toLowerCase()
    if (
      contentType.startsWith('text/') ||
      contentType === 'application/json' ||
      contentType.endsWith('+json') ||
      contentType === 'application/xhtml+xml'
    ) {
      throw new ReportFileError('INVALID_FILE')
    }

    const buffer = await response.arrayBuffer()
    if (!isCurrent()) return
    throwIfAborted()
    const bytes = new Uint8Array(buffer)
    // xlsxはZIP形式。空ファイルやMIMEを誤ったJSON/HTML応答の保存を防ぐ。
    if (
      bytes.length < 4 ||
      bytes[0] !== 0x50 ||
      bytes[1] !== 0x4b ||
      bytes[2] !== 0x03 ||
      bytes[3] !== 0x04
    ) {
      throw new ReportFileError('INVALID_FILE')
    }

    const blob = new Blob([buffer], {
      type:
        contentType ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const objectURL = URL.createObjectURL(blob)
    let anchor: HTMLAnchorElement | undefined
    try {
      if (!isCurrent()) return
      throwIfAborted()
      anchor = document.createElement('a')
      anchor.href = objectURL
      anchor.download = file.filename
      anchor.style.display = 'none'
      document.body.appendChild(anchor)
      anchor.click()
    } finally {
      anchor?.remove()
      URL.revokeObjectURL(objectURL)
    }
  } catch (error) {
    if (
      error instanceof ReportFileError ||
      (error instanceof Error && error.name === 'AbortError') ||
      options.signal?.aborted
    ) {
      throw error
    }
    throw new ReportFileError('DOWNLOAD_FAILED')
  }
}
